<template>
  <div
    v-if="loading || error"
    class="col-12"
  >
    <div class="row justify-center items-center">
      <div class="col-auto">
        <div
          v-if="error"
          class="text-center"
        >
          <q-icon
            color="negative"
            name="info"
          />
          Error occurred <br>
          <q-btn
            icon="refresh"
            label="Try again"
            class="q-mt-xs"
            @click="init()"
          />
        </div>
        <q-spinner-puff
          v-else-if="loading"
          color="primary"
          size="60px"
        />
      </div>
    </div>
  </div>
  <div
    v-else
    class="col-12"
  >
    <div class="row gutter-md full-height">
      <slot
        name="before-timeline"
        :model="model"
      />
      <div class="col">
        <q-card class="full-height">
          <q-card-title>
            {{ timelineTitle }}
            <q-icon
              v-if="actions && actions.length"
              slot="right"
              v-tippy
              name="more_vert"
              title="More"
            >
              <q-popover>
                <q-list
                  link
                  class="no-border"
                >
                  <q-item
                    v-for="(action, actionIndex) in actions"
                    :key="actionIndex"
                    v-close-overlay
                    :disabled="action.disabled"
                    @click.native="() => !action.disabled && action.handler ? action.handler(model) : ''"
                  >
                    <a
                      v-if="action.url"
                      :href="action.url"
                      style="text-decoration: none"
                    >
                      <q-item-main>
                        {{ action.label }}
                      </q-item-main>
                    </a>
                    <q-item-main v-else>
                      {{ action.label }}
                    </q-item-main>
                  </q-item>
                </q-list>
              </q-popover>
            </q-icon>
          </q-card-title>
          <q-card-main id="card-main-timeline">
            <q-timeline
              v-if="activities.length !== 0"
              color="secondary"
            >
              <q-timeline-entry
                v-for="(activity, activityIndex) in activities"

                :key="activity.id"
                :title="activity.title"
                :side="activityIndex % 2 === 0 ? 'left' : 'right'"
                :class="activity.type"
              >
                <SdDatetime
                  slot="subtitle"
                  :value="activity.created_at"
                  :market-id="marketId"
                  :timezone="timezone"
                  class="q-timeline-subtitle"
                />
                <q-chip
                  v-if="has(activity, 'data.summary.email')"
                  dense
                  class="py-1 px-2 mx-1"
                  :color="chipColor(activity.data.summary.email)"
                  text-color="white"
                  icon="mail"
                >
                  Email
                </q-chip>
                <q-chip
                  v-if="has(activity, 'data.summary.sms')"
                  dense
                  class="py-1 px-2 mx-1"
                  :color="chipColor(activity.data.summary.sms)"
                  text-color="white"
                  icon="sms"
                >
                  SMS
                </q-chip>
                <q-chip
                  v-if="has(activity, 'data.summary.push')"
                  dense
                  class="py-1 px-2 mx-1"
                  :color="chipColor(activity.data.summary.push)"
                  text-color="white"
                  icon="notifications"
                >
                  Push Notification
                </q-chip>
                <q-chip
                  v-if="has(activity, 'data.success') && activity.data.success === false"
                  dense
                  class="py-1 px-2 mx-1"
                  color="red"
                  text-color="white"
                  icon="error"
                >
                  Failed Delivery
                </q-chip>
                <!-- will be refactored after https://linear.app/showdigs/issue/SHO-2614 -->
                <q-chip
                  v-if="has(activity, 'data.success') &&
                    activity.data.success === true &&
                    !get(activity, 'data.summary.sms') &&
                    !get(activity, 'data.summary.push') &&
                    !get(activity, 'data.summary.email')"
                  dense
                  class="py-1 px-2 mx-1"
                  color="green"
                  text-color="white"
                  icon="info"
                >
                  Fallback Success
                </q-chip>
                <div class="row gutter-xs items-center">
                  <div class="col-auto">
                    <img
                      v-if="activity.causer"
                      :src="activity.causer.avatar_file_url"
                      style="width: 40px;"
                      class="float-left round"
                    >
                    <img
                      v-else
                      src="statics/icons/favicon-96x96.png"
                      style="width: 40px;"
                      class="float-left round"
                    >
                  </div>
                  <div
                    class="col"
                    v-html="activity.content"
                  />
                </div>
              </q-timeline-entry>
            </q-timeline>
            <h4
              v-else-if="activities.length === 0"
              class="text-center"
            >
              No activities found
            </h4>
            <infinite-loading
              v-if="!hideInfiniteScroll"
              @infinite="fetchMoreActivities"
            >
              <q-spinner-oval
                slot="spinner"
                :size="40"
              />
            </infinite-loading>
          </q-card-main>
        </q-card>
      </div>
    </div>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading';
import has from 'lodash.has';
import get from 'lodash.get';

export default {
  components: {
    InfiniteLoading,
  },
  props: {
    className: {
      type: String,
      required: false,
    },
    id: {
      type: [Number, String],
      required: false,
    },
    timelineTitle: {
      type: String,
      required: false,
      default: 'Timeline',
    },
    actions: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      activities: [],
      model: {},
      page: 1,
      loading: true,
      error: false,
      hideInfiniteScroll: false,
      marketId: undefined,
      timezone: undefined,
      has,
      get,
    };
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      this.loading = true;
      this.error = false;
      this.page = 1;

      this.fetchActivities()
        .then(({ activities, model }) => {
          this.activities = activities.data;
          this.model = model;
          this.page++;
          this.loading = false;

          if (activities.last_page === 1) {
            this.hideInfiniteScroll = true;
          }

          if (model && model.property) {
            this.marketId = model.property.market_id;
            this.timezone = model.property.timezone;
          }

          const channelName = this.id ? `timeline-${this.className}-${model.id}` : `timeline-${this.className}`;
          window.PusherInstance
            .subscribe(channelName.toLowerCase())
            .bind('activity', (event) => this.pusherEventReceived(event));
          this.$emit('loaded', model);
        }).catch((error) => {
          this.error = error;
          this.$emit('error', error);
        });
    },
    pusherEventReceived(event) {
      const dupCheck = {
        status: false,
      };
      this.activities.map((activity) => {
        if (activity.id === event.activity.id) {
          dupCheck.status = true;
        }
      });
      if (!dupCheck.status) {
        this.activities.unshift(event.activity);
      }
    },
    fetchActivities(page = 1) {
      const url = this.id ? `${this.className}/${this.id}/activities` : `${this.className}/activities`;
      return this.api().request('GET', url, {}, { page });
    },
    fetchMoreActivities($state) {
      this.fetchActivities(this.page).then((response) => {
        this.activities = this.activities.concat(response.activities.data);

        $state.loaded();

        if (response.activities.last_page <= this.page) {
          $state.complete();
        } else {
          this.page++;
        }
      }).catch(() => {
        $state.error();
      });
    },
    chipColor(success) {
      return success ? 'green' : 'red';
    },
  },
};
</script>

<style lang="scss" scoped>
.q-item[disabled]{
  color: var(--q-color-light-d);
  &:hover{
    background: none;
  }
}
</style>
